import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import { DesignFile, BOM, DesignTask } from '@/types/design';

interface DesignState {
  files: DesignFile[];
  boms: BOM[];
  tasks: DesignTask[];
  isLoading: boolean;
  error: string | null;
  fetchBOMs: (projectId: string) => Promise<void>;
  createBOM: (data: Partial<BOM>) => Promise<void>;
  updateBOM: (id: string, data: Partial<BOM>) => Promise<void>;
  deleteBOM: (id: string) => Promise<void>;
}

export const useDesignStore = create<DesignState>((set, get) => ({
  files: [],
  boms: [],
  tasks: [],
  isLoading: false,
  error: null,

  fetchBOMs: async (projectId: string) => {
    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('boms')
        .select(`
          *,
          items:bom_items(*)
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ boms: data || [] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch BOMs';
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  createBOM: async (data: Partial<BOM>) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('boms')
        .insert([data]);

      if (error) throw error;

      // Refresh BOMs list
      if (data.project_id) {
        await get().fetchBOMs(data.project_id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create BOM';
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  updateBOM: async (id: string, data: Partial<BOM>) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('boms')
        .update(data)
        .eq('id', id);

      if (error) throw error;

      // Refresh BOMs list if project_id is available
      const bom = get().boms.find(b => b.id === id);
      if (bom?.project_id) {
        await get().fetchBOMs(bom.project_id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update BOM';
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  deleteBOM: async (id: string) => {
    try {
      set({ isLoading: true, error: null });
      
      // Get project_id before deletion for refreshing
      const bom = get().boms.find(b => b.id === id);
      const projectId = bom?.project_id;

      const { error } = await supabase
        .from('boms')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh BOMs list if project_id was found
      if (projectId) {
        await get().fetchBOMs(projectId);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete BOM';
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  }
}));

// Set up realtime subscription
supabase
  .channel('design_changes')
  .on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'boms' },
    (payload) => {
      const projectId = payload.new?.project_id;
      if (projectId) {
        useDesignStore.getState().fetchBOMs(projectId);
      }
    }
  )
  .subscribe();